import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  Box,
  Typography,
  Divider,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { Close as CloseIcon, Image as ImageIcon } from '@mui/icons-material';
import { SocialPlatform } from '../types';
import { useAuth } from '../contexts/AuthContext';
import {
  availablePlatforms,
  getPlatform,
  strictestCharLimit,
} from '../config/platforms';
import { PlatformSchedule, ScheduleVariants, pastScheduleEntries } from './PlatformSchedule';
import { PerPlatformCopy, ContentVariants, overLimitPlatforms } from './PerPlatformCopy';
import { useIsMobile } from '../hooks/useIsMobile';
import { thumbnailUrl } from '../utils/media';
import { InstagramImageCheck } from './InstagramImageCheck';

const API_URL = process.env.REACT_APP_API_URL || '';

export interface EditablePost {
  id: string;
  content: string;
  platforms: string[];
  media_urls?: string[];
  scheduled_for?: string | null;
  /** Per-platform publish times. Absent platforms use scheduled_for. */
  schedule_variants?: ScheduleVariants | null;
  /** Fingerprint from the server; sent back so a save from a stale copy is refused. */
  version?: string;
  /** Per-platform captions. Absent platforms use `content`. */
  content_variants?: ContentVariants | null;
  status: string;
}

interface Props {
  open: boolean;
  post: EditablePost;
  onClose: () => void;
  /** Called after a successful save, so the caller can refresh its list. */
  onSaved: () => void;
}

/**
 * Edit an existing post's content, platforms and media in place.
 *
 * A dialog rather than a trip to the composer, so editing a post found in History or on the
 * Calendar does not lose the reader's place. The same rules the composer enforces are
 * applied here — over-limit content and Instagram-without-media are blocked before
 * submitting, because the server refuses them and a silent 422 reads as a broken button.
 */
export const EditPostDialog: React.FC<Props> = ({ open, post, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const { token, user } = useAuth();
  const [content, setContent] = useState(post.content);
  const [platforms, setPlatforms] = useState<string[]>(post.platforms);
  const [media, setMedia] = useState<string[]>(post.media_urls || []);
  const [when, setWhen] = useState<Date | null>(
    post.scheduled_for ? new Date(post.scheduled_for) : null
  );
  const [scheduleVariants, setScheduleVariants] = useState<ScheduleVariants>(
    post.schedule_variants || {}
  );
  const [variants, setVariants] = useState<ContentVariants>(post.content_variants || {});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // A demo account is restricted server-side; do not offer what would be refused
  const offered = availablePlatforms().filter(
    p => !user?.allowedPlatforms || user.allowedPlatforms.includes(p.id)
  );

  const selected = platforms.map(p => getPlatform(p)).filter(Boolean) as NonNullable<
    ReturnType<typeof getPlatform>
  >[];
  // The limit the shared caption is actually held to: the strictest among platforms that do
  // NOT have their own version. Give X its own text and LinkedIn's 3,000 takes over.
  const governing = selected.filter(p => !variants[p.id]?.trim());
  const limit = governing.length
    ? Math.min(...governing.map(p => p.charLimit))
    : strictestCharLimit(platforms);
  // Measured per platform against the text that platform will actually receive. Measuring the
  // shared caption against every limit made a post with a short X version unsaveable.
  const overLimit = overLimitPlatforms(platforms, content, variants);
  const missingMedia = selected.filter(p => p.requiresMedia && media.length === 0);

  // Only a scheduled post has times to change. A draft is scheduled from Post History or the
  // Calendar, which is where the Schedule action lives.
  const scheduled = post.status === 'scheduled';
  const pastPlatforms = pastScheduleEntries(platforms, scheduleVariants);
  const basePast = when !== null && when.getTime() <= Date.now();

  const togglePlatform = (id: SocialPlatform) =>
    setPlatforms(prev => (prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]));

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setError('');
    setUploading(true);
    try {
      const response = await fetch(`${API_URL}/api/media`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': file.type },
        body: file,
      });
      const data = await response.json();
      if (response.ok) {
        setMedia(prev => [...prev, data.url]);
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch {
      setError('Network error uploading file');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setError('');

    if (!content.trim()) {
      setError('Post content cannot be empty');
      return;
    }
    if (platforms.length === 0) {
      setError('Select at least one platform');
      return;
    }
    if (overLimit.length) {
      setError(
        `Too long for ${overLimit.map(p => `${p.name} (${p.charLimit.toLocaleString()})`).join(', ')}. ` +
        'Shorten it, give those platforms their own shorter version below, or deselect them.'
      );
      return;
    }
    if (missingMedia.length) {
      setError(`${missingMedia.map(p => p.name).join(', ')} requires an image or video.`);
      return;
    }
    if (scheduled && basePast) {
      setError('The publish time has already passed. Choose a future time.');
      return;
    }
    if (scheduled && pastPlatforms.length) {
      setError(
        `These times have already passed: ${pastPlatforms
          .map(p => getPlatform(p)?.name || p)
          .join(', ')}. Choose future times.`
      );
      return;
    }

    setSaving(true);
    try {
      // PUT replaces the whole row, so every field is resent — omitting `platforms` here
      // would clear it.
      const response = await fetch(`${API_URL}/api/posts/${post.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content,
          platforms,
          // Must be sent even when empty. The server replaces the whole column on a PUT, so
          // omitting this wrote NULL and destroyed the per-platform captions on every save.
          contentVariants: Object.fromEntries(
            Object.entries(variants).filter(([, text]) => text.trim())
          ),
          mediaUrls: media,
          scheduledFor: scheduled && when ? when.toISOString() : post.scheduled_for || null,
          // Per-network times go with the rest of the edit. A draft sent back to Idea Staging
          // keeps the times it had, so they are returned untouched — the server replaces the
          // whole column on a PUT, and null here wiped them on every text edit. They stay
          // inert because the status below remains 'draft'.
          scheduleVariants: scheduled ? scheduleVariants : post.schedule_variants || null,
          // Keep the post where it was: editing a scheduled post should not silently
          // unschedule it, and editing a failed one should not mark it a draft.
          status: post.status === 'scheduled' ? 'scheduled' : 'draft',
          // Refused with 409 if the post changed after this dialog loaded it, rather than
          // putting the old copy back over the newer one
          version: post.version,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        setError(data.message || data.error || 'Could not save the post');
        return;
      }
      onSaved();
      onClose();
    } catch {
      setError('Network error saving the post');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog fullScreen={isMobile} open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit post</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          fullWidth
          multiline
          minRows={5}
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="What do you want to say?"
          sx={{ mb: 1 }}
        />
        <Typography
          variant="caption"
          color={limit !== null && content.length > limit ? 'error.main' : 'text.secondary'}
        >
          {content.length.toLocaleString()}
          {limit !== null && ` / ${limit.toLocaleString()}`} characters
        </Typography>

        {/* Shown inline under the counter rather than behind a toggle: the point is that an
            over-limit platform is fixable without shortening the others. A post that already
            has a shorter version for a network shows it here to be edited. */}
        <PerPlatformCopy
          platforms={platforms}
          content={content}
          variants={variants}
          onChange={setVariants}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Platforms</Typography>
        <FormGroup row>
          {offered.map(p => (
            <FormControlLabel
              key={p.id}
              control={
                <Checkbox
                  checked={platforms.includes(p.id)}
                  onChange={() => togglePlatform(p.id)}
                />
              }
              label={p.name}
            />
          ))}
        </FormGroup>

        {overLimit.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Too long for {overLimit.map(p => `${p.name} (${p.charLimit.toLocaleString()})`).join(', ')}.
          </Alert>
        )}
        {missingMedia.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {missingMedia.map(p => p.name).join(', ')} requires an image or video.
          </Alert>
        )}

        {scheduled && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Timing</Typography>
            <PlatformSchedule
              platforms={platforms}
              base={when}
              variants={scheduleVariants}
              onBaseChange={setWhen}
              onVariantsChange={setScheduleVariants}
              disabled={saving}
              // So the same-day note does not report this post against itself.
              postId={post.id}
            />
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>Media</Typography>
          <Button
            size="small"
            startIcon={<ImageIcon />}
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            Add
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            hidden
            onChange={handleUpload}
          />
        </Box>
        {uploading && <LinearProgress sx={{ mb: 1 }} />}
        {media.length === 0 ? (
          <Typography variant="caption" color="text.secondary">No media attached.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {media.map(url => (
              <Box key={url} sx={{ position: 'relative' }}>
                <Box
                  component="img"
                  src={thumbnailUrl(url, 320)}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  sx={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 1 }}
                />
                <IconButton
                  size="small"
                  onClick={() => setMedia(prev => prev.filter(m => m !== url))}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
        {platforms.includes('instagram') && media.length > 0 && (
          <InstagramImageCheck
            urls={media}
            onReplace={(oldUrl, fitted) => setMedia(prev => prev.map(m => (m === oldUrl ? fitted.url : m)))}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || uploading}>
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
